/**
 * Admin Roadmap Service — typed wrappers over /api/admin/roadmap/* endpoints.
 *
 * Backend source of truth: routers/admin_roadmap.py. Keep response types in
 * sync with AdminItemSummary / AdminCompositionResponse there.
 */
import { adminFetch, adminMutate } from '../utils/adminApi';

// ── Types mirroring routers/admin_roadmap.py ────────────────────────

export type RoadmapStatus =
  | 'under_review'
  | 'planned'
  | 'in_progress'
  | 'shipped'
  | 'declined'
  | 'duplicate'
  // Phase 1 additions (migration 1360)
  | 'intake'
  | 'todo'
  | 'qa'
  // Phase 6 polish (migration 1425) — staging-environment workflow stage
  | 'stage';

// Phase 1 (migration 1361) — provenance values. 'slack' reserved for Phase 3.
export type RoadmapSource = 'admin_manual' | 'in_app_form' | 'support_form' | 'slack';

// Phase 4 (migration 1418) — Slack triage severity. NULL on AdminRoadmapItem
// for features and admin-authored items. Set by ingester from leading dot
// emoji on Slack messages, or manually by admin via editor modal.
export type RoadmapSeverity = 'critical' | 'important' | 'review';

export type RoadmapVisibility = 'public' | 'internal_only' | 'hidden';

export type RoadmapCategory = 'feature' | 'improvement' | 'known_issue';

export interface AdminRoadmapItem {
  id: string;
  title: string;
  body: string;
  category: RoadmapCategory;
  status: RoadmapStatus;
  visibility: RoadmapVisibility;
  pinned: boolean;
  is_admin_authored: boolean;
  vote_count: number;
  comment_count: number;
  release_notes: string | null;
  merged_into_id: string | null;
  shipped_at: string | null;
  created_at: string;
  updated_at: string;
  source_ticket_count: number;
  // Phase 1 (migration 1361) — curation + provenance. All optional.
  user_story?: string | null;
  success_criteria?: string | null;
  estimate?: string | null;
  internal_notes?: string | null;
  source?: RoadmapSource | null;
  source_reference?: string | null;
  submitted_by_user_id?: string | null;
  promoted_at?: string | null;
  // Phase 4 (migration 1418)
  severity?: RoadmapSeverity | null;
  needs_triage?: boolean;
  // Phase 5 (migration 1419) — TODAY/BACKLOG split + Kanban archive
  is_today?: boolean;
  archived_from_kanban_at?: string | null;
  // Phase 6 polish v24 (migration 1496) — TOMORROW lane between TODAY
  // and BACKLOG. ISO timestamp when admin pinned the item to tomorrow's
  // plan; NULL = item is in TODAY/NEXT UP/BACKLOG. Auto-promoted to
  // TODAY by the midnight-ET rollover cron.
  tomorrow_set_at?: string | null;
  // Phase 6 polish v37 (migration 1598) — NEXT UP lane between TOMORROW
  // and BACKLOG. ISO timestamp when admin pinned the item to NEXT UP.
  // NULL = item is in TODAY/TOMORROW/BACKLOG. Unlike TOMORROW, NEXT UP
  // does NOT auto-promote — manual moves only.
  next_up_set_at?: string | null;
  // Phase 6 polish v12 (migration 1441) — owner slack_handle (NULL = unassigned).
  assignee?: string | null;
  // Migration 1731 — manual drag-to-reorder ranks. NULL = never dragged
  // (falls back to the status/pinned/created order). list_manual_rank orders
  // within a List lane; kanban_manual_rank within a Kanban status column.
  list_manual_rank?: number | null;
  kanban_manual_rank?: number | null;
}

export interface AdminRoadmapSourceTicket {
  ticket_id: string;
  ticket_number: number;
  subject: string | null;
  linked_at: string;
}

export interface AdminRoadmapItemDetail extends AdminRoadmapItem {
  source_tickets: AdminRoadmapSourceTicket[];
}

export interface AdminRoadmapListResponse {
  items: AdminRoadmapItem[];
  total: number;
}

export interface AdminCompositionResponse {
  public: {
    total: number;
    by_category: Record<string, number>;
    by_status: Record<string, number>;
  };
  internal: {
    total: number;
    by_category: Record<string, number>;
    by_status: Record<string, number>;
  };
  pct_new_or_improved_public: number;
  warning_if_promoting_known_issue: boolean;
}

export interface ItemCreateBody {
  title: string;
  body: string;
  category: RoadmapCategory;
  visibility: RoadmapVisibility;
  status: RoadmapStatus;
  pinned?: boolean;
}

export interface ItemPatchBody {
  title?: string;
  body?: string;
  category?: RoadmapCategory;
  visibility?: RoadmapVisibility;
  status?: RoadmapStatus;
  release_notes?: string;
  pinned?: boolean;
  note?: string;
  // Phase 1 (migration 1361) — optional curation fields.
  user_story?: string;
  success_criteria?: string;
  estimate?: string;
  internal_notes?: string;
  // Phase 4 (migration 1418) — admin can manually set/clear severity
  // (empty string clears) and clear needs_triage once classified.
  severity?: RoadmapSeverity | '';
  needs_triage?: boolean;
  // Phase 5 (migration 1419) — admin toggles is_today inline from the
  // List view to move items between TODAY focus + BACKLOG sections.
  is_today?: boolean;
  // Phase 6 polish v24 (migration 1496) — TOMORROW lane toggle. True
  // moves the item into TOMORROW (backend stamps NOW() in
  // tomorrow_set_at); False removes it. Backend enforces mutual
  // exclusion with is_today + is_next_up.
  is_tomorrow?: boolean;
  // Phase 6 polish v37 (migration 1598) — NEXT UP lane toggle. True
  // pins the item to NEXT UP (backend stamps NOW() in next_up_set_at);
  // False unpins. Backend enforces mutual exclusion with is_today +
  // is_tomorrow. Unlike TOMORROW, NEXT UP does NOT auto-promote.
  is_next_up?: boolean;
  // Phase 6 polish v12 (migration 1441) — owner slack_handle. Empty
  // string clears the column to NULL (unassigned).
  assignee?: string;
}

export interface PromoteFromTicketBody {
  title?: string;
  body?: string;
  category: RoadmapCategory;
  visibility?: RoadmapVisibility;
  status?: RoadmapStatus;
}

// ── Phase 6: Internal comments + image attachments ──────────────────

export interface CommentAttachment {
  id: string;
  content_type: string;
  size_bytes: number;
  // Signed URL valid for 15 min, regenerated on each thread render
  preview_url: string;
}

/**
 * Phase 6 polish v26 (2026-05-13, migration 1499): item-level attachment
 * shape. Distinct from CommentAttachment — these are pulled from Slack
 * on ingest (or future direct admin upload), not attached to a comment.
 * preview_url is a fresh signed S3 URL (15-min expiry).
 */
export interface ItemAttachment {
  id: string;
  source_provenance: 'slack_ingest' | 'admin_upload';
  source_filename: string | null;
  content_type: string;
  size_bytes: number;
  uploaded_at: string;
  preview_url: string;
}

export interface RoadmapComment {
  id: string;
  roadmap_item_id: string;
  author_admin_id: string | null;
  author_display_name: string | null;
  // Phase 6 polish (2026-05-11, migration 1424): human author name when
  // the shared admin@ login is used. Frontend prefers this over the
  // admin_users.email join for the row header.
  author_persona: string | null;
  body: string;
  created_at: string;
  edited_at: string | null;
  attachments: CommentAttachment[];
}

export interface CommentCreateBody {
  body: string;
  attachment_ids?: string[];
  // Phase 6 polish — author persona override from the composer's
  // "Posting as" selector.
  author_persona?: string;
}

export interface CommentEditBody {
  body: string;
  author_persona?: string;
}

export interface AttachmentUploadResponse {
  attachment_id: string;
  s3_key: string;
  preview_url: string;
}

// Phase 6 polish (2026-05-11): @-mention autocomplete source. Returned
// by GET /api/admin/roadmap/mentions/handles.
export interface MentionHandle {
  handle: string;
  display_name: string | null;
}


// ── Filter params ───────────────────────────────────────────────────

export interface ListParams {
  status?: RoadmapStatus | '';
  category?: RoadmapCategory | '';
  visibility?: RoadmapVisibility | '';
  source?: RoadmapSource | '';
  search?: string;
  include_deleted?: boolean;
  // Phase 5 (migration 1419)
  include_shipped?: boolean;  // false by default — /completed page sets true
  only_today?: boolean;       // true → TODAY focus list only
  // Phase 6 polish v6 (2026-05-11) — declined items also hidden by
  // default; the Declined tab sets this true with status='declined'.
  include_declined?: boolean;
  // Phase 6 polish v19 (2026-05-13) — "New" view tab: filter to items
  // created within the past N hours. Backend clamps to [1, 168] (1h to
  // 1 week). Null/omitted → no recency filter.
  created_within_hours?: number;
  limit?: number;
  offset?: number;
}

function buildQuery(params: ListParams): string {
  const q = new URLSearchParams();
  if (params.status) q.set('status', params.status);
  if (params.category) q.set('category', params.category);
  if (params.visibility) q.set('visibility', params.visibility);
  if (params.source) q.set('source', params.source);
  if (params.search) q.set('search', params.search);
  if (params.include_deleted) q.set('include_deleted', 'true');
  if (params.include_shipped) q.set('include_shipped', 'true');
  if (params.include_declined) q.set('include_declined', 'true');
  if (params.only_today) q.set('only_today', 'true');
  if (params.created_within_hours != null) {
    q.set('created_within_hours', String(params.created_within_hours));
  }
  if (params.limit != null) q.set('limit', String(params.limit));
  if (params.offset != null) q.set('offset', String(params.offset));
  const s = q.toString();
  return s ? `?${s}` : '';
}

// ── Service methods ─────────────────────────────────────────────────

export const adminRoadmapService = {
  list: (params: ListParams = {}) =>
    adminFetch<AdminRoadmapListResponse>(
      `/api/admin/roadmap/items${buildQuery(params)}`,
    ),

  detail: (itemId: string) =>
    adminFetch<AdminRoadmapItemDetail>(`/api/admin/roadmap/items/${itemId}`),

  composition: () =>
    adminFetch<AdminCompositionResponse>('/api/admin/roadmap/composition'),

  create: (body: ItemCreateBody) =>
    adminMutate<AdminRoadmapItem>('/api/admin/roadmap/items', 'POST', body),

  promoteFromTicket: (ticketId: string, body: PromoteFromTicketBody) =>
    adminMutate<AdminRoadmapItem>(
      `/api/admin/roadmap/items/from-ticket/${ticketId}`,
      'POST',
      body,
    ),

  // Look up whether a support ticket has been promoted. Returns the
  // linked roadmap item if any, else { item: null }. Used by the ticket
  // detail UI to swap Promote → "Already on Roadmap →" state.
  getSourceForTicket: (ticketId: string) =>
    adminFetch<{ item: AdminRoadmapItem | null }>(
      `/api/admin/roadmap/items/source-for-ticket/${ticketId}`,
    ),

  patch: (itemId: string, body: ItemPatchBody) =>
    adminMutate<AdminRoadmapItem>(
      `/api/admin/roadmap/items/${itemId}`,
      'PATCH',
      body,
    ),

  // Migration 1731 — persist a hand-dragged order for one List lane or Kanban
  // column. orderedIds is the FULL ordered id list of that partition after
  // the drop; the backend rewrites evenly-spaced ranks over exactly those ids.
  reorder: (scope: 'list' | 'kanban', orderedIds: string[]) =>
    adminMutate<{ success: boolean; scope: string; reordered: number }>(
      '/api/admin/roadmap/items/reorder',
      'POST',
      { scope, ordered_ids: orderedIds },
    ),

  remove: (itemId: string) =>
    adminMutate<{ deleted: boolean }>(
      `/api/admin/roadmap/items/${itemId}`,
      'DELETE',
    ),

  merge: (itemId: string, canonicalId: string) =>
    adminMutate<AdminRoadmapItem>(
      `/api/admin/roadmap/items/${itemId}/merge`,
      'POST',
      { canonical_id: canonicalId },
    ),

  linkTicket: (itemId: string, ticketId: string) =>
    adminMutate<{ linked: boolean; source_ticket_count: number }>(
      `/api/admin/roadmap/items/${itemId}/link-ticket/${ticketId}`,
      'POST',
    ),

  // ── Phase 6: Internal comments ────────────────────────────────────

  /** List internal admin comments for an item, chronological oldest-first. */
  listComments: (itemId: string) =>
    adminFetch<RoadmapComment[]>(
      `/api/admin/roadmap/items/${itemId}/comments`,
    ),

  /**
   * Phase 6 polish v26 (2026-05-13, migration 1499): list ITEM-level
   * attachments (files pulled from Slack on ingest). Distinct from
   * `listComments` — those return comment-level attachments. preview_url
   * on each entry is a fresh signed S3 URL (15-min expiry).
   */
  listItemAttachments: (itemId: string) =>
    adminFetch<ItemAttachment[]>(
      `/api/admin/roadmap/items/${itemId}/attachments`,
    ),

  /**
   * Phase 6 polish (2026-05-11): list configured @-mention handles
   * for the composer's autocomplete popover. Sourced from admin_users
   * WHERE slack_handle IS NOT NULL — only admins who can be DM'd by
   * the Phase 7 notifier appear in the picker.
   */
  listMentionHandles: () =>
    adminFetch<MentionHandle[]>(
      `/api/admin/roadmap/mentions/handles`,
    ),

  /** Create a new comment. attachment_ids reference pre-uploaded images. */
  createComment: (itemId: string, body: CommentCreateBody) =>
    adminMutate<RoadmapComment>(
      `/api/admin/roadmap/items/${itemId}/comments`,
      'POST',
      body,
    ),

  /** Edit own comment. 403 on someone else's. */
  editComment: (commentId: string, body: CommentEditBody) =>
    adminMutate<RoadmapComment>(
      `/api/admin/roadmap/comments/${commentId}`,
      'PATCH',
      body,
    ),

  /** Soft-delete own comment. 403 on someone else's. */
  deleteComment: (commentId: string) =>
    adminMutate<{ deleted: boolean }>(
      `/api/admin/roadmap/comments/${commentId}`,
      'DELETE',
    ),

  /** Upload a single image attachment. Returns id + signed URL. */
  uploadAttachment: async (file: File): Promise<AttachmentUploadResponse> => {
    const formData = new FormData();
    formData.append('file', file);
    // adminMutate sends JSON; for multipart we use fetch directly
    // matching the pattern in adminMutate (credentials etc).
    const resp = await fetch('/api/admin/roadmap/comments/upload', {
      method: 'POST',
      body: formData,
      credentials: 'include',
    });
    if (!resp.ok) {
      const text = await resp.text();
      throw new Error(`Upload failed: ${resp.status} ${text}`);
    }
    return resp.json() as Promise<AttachmentUploadResponse>;
  },
};

// ── Label helpers (match public-facing labels from routers/roadmap.py) ─

export const STATUS_LABEL: Record<RoadmapStatus, string> = {
  under_review: 'Under Review',
  planned: 'Planned',
  in_progress: 'In Progress',
  shipped: 'Shipped',
  declined: 'Declined',
  duplicate: 'Duplicate',
  // Phase 1 (migration 1360) — admin-only labels. Never shown to end users.
  intake: 'Intake',
  todo: 'To Do',
  qa: 'QA',
  // Phase 6 polish (migration 1425) — staging environment workflow stage.
  stage: 'Stage',
};

// Phase 4 polish v9 (2026-05-11): renamed category display labels
// per user feedback — "Feature / Refine / Bug" reads punchier than
// "Feature / Improvement / Known Issue". Underlying DB enum values
// (`feature` / `improvement` / `known_issue`) are UNCHANGED so the
// backend VALID_CATEGORIES set, the Slack ingester's keyword
// classifier, and migration 1200's CHECK constraint all stay stable.
export const CATEGORY_LABEL: Record<RoadmapCategory, string> = {
  feature: 'Feature',
  improvement: 'Refine',
  known_issue: 'Bug',
};

export const VISIBILITY_LABEL: Record<RoadmapVisibility, string> = {
  public: 'Public',
  internal_only: 'Internal',
  hidden: 'Hidden',
};

// Phase 6 polish v11 (2026-05-11): coherent status palette redesigned
// as a workflow progression (cool → warm → green). Each hue is
// distinct from the next so adjacent stages don't blur. Color
// semantics:
//   slate    →  fresh / unsorted (intake)
//   amber    →  waiting on decision (under_review)
//   blue     →  scheduled (planned)
//   indigo   →  actively working (in_progress)
//   teal     →  pre-prod / staging (stage)
//   orange   →  validation (qa)
//   green    →  live / done (shipped)
//   red      →  off-path / rejected (declined)
//   muted    →  admin-set / deprecated (duplicate, todo)
//
// Previously: lots of overlap with category + severity palettes (e.g.
// intake-amber clashed with known_issue-amber + important-amber dot).
// Now status pills own the workflow gradient and the other axes use
// distinct visual treatments (outline categories, RGB dots for
// severity).
export const STATUS_COLORS: Record<RoadmapStatus, { bg: string; fg: string }> = {
  intake:        { bg: '#F1F5F9', fg: '#475569' },  // slate — new, unsorted
  under_review:  { bg: '#FEF3C7', fg: '#92400E' },  // amber — decision pending
  planned:       { bg: '#DBEAFE', fg: '#1E40AF' },  // blue — scheduled
  in_progress:   { bg: '#E0E7FF', fg: '#3730A3' },  // indigo — actively working
  // Phase 6 (migration 1425): staging-env step in the workflow.
  stage:         { bg: '#CCFBF1', fg: '#115E59' },  // teal — pre-prod
  qa:            { bg: '#FFEDD5', fg: '#9A3412' },  // orange — validation
  shipped:       { bg: '#BBF7D0', fg: '#14532D' },  // deep green — live
  declined:      { bg: '#FEE2E2', fg: '#991B1B' },  // red — rejected
  duplicate:     { bg: '#F9FAFB', fg: '#9CA3AF' },  // very muted — admin-set
  todo:          { bg: '#F3F4F6', fg: '#9CA3AF' },  // very muted — deprecated UI
};

// Phase 6 polish v11 (2026-05-11): category pills now render as
// OUTLINED badges to be visually distinct from filled status pills.
// The `bg` is a very pale tint of the hue (kept for hover/fallback);
// the `fg` is the strong text + border color. Component renders
// `1px solid <fg>` border with the pale `bg` as the fill.
//
// Semantic colors per the Slack-dot convention:
//   feature      → blue   (matches 🔵 Slack dot for features)
//   improvement  → purple (transformation / refinement)
//   known_issue  → red    (matches 🔴 — bugs are urgent)
//
// "Bug" pill (red) sits adjacent to "Declined" status pill (red) on
// edge-case rows but they're distinguishable by treatment: status
// is FILLED, category is OUTLINED.
export const CATEGORY_COLORS: Record<RoadmapCategory, { bg: string; fg: string }> = {
  feature:     { bg: '#EFF6FF', fg: '#1E40AF' },  // blue outline (🔵 Slack)
  improvement: { bg: '#F5F3FF', fg: '#6B21A8' },  // purple outline (transformation)
  known_issue: { bg: '#FEF2F2', fg: '#B91C1C' },  // red outline (🔴 urgency)
};

export const VISIBILITY_COLORS: Record<RoadmapVisibility, { bg: string; fg: string }> = {
  public: { bg: '#D1FAE5', fg: '#059669' },
  internal_only: { bg: '#F3F4F6', fg: '#6B7280' },
  hidden: { bg: '#FEE2E2', fg: '#991B1B' },
};

// Phase 1 (migration 1361)
export const SOURCE_LABEL: Record<RoadmapSource, string> = {
  admin_manual: 'Admin',
  in_app_form: 'In-App',
  support_form: 'Support',
  slack: 'Slack',
};

// Phase 4 (migration 1418) — severity palette for the card stripe.
// Red/amber/green follow conventional bug-priority colors. The palette
// matches the Slack dot emoji users see when posting in #backlog so the
// visual link is clear.
// Phase 4 polish v8 (2026-05-11): renamed display labels from
// Critical/Important/Review → Critical/Medium/Low per user feedback.
// The underlying DB enum values (`critical`/`important`/`review`) are
// UNCHANGED — only the display strings are remapped. This keeps the
// Slack-dot mapping (🔴/🟡/🟢), the backend VALID_SEVERITIES set,
// the keyword classifier, and migration 1418's CHECK constraint
// stable. Future severity scale: Critical (red) / Medium (yellow) /
// Low (green) / Feature (blue, no severity).
export const SEVERITY_LABEL: Record<RoadmapSeverity, string> = {
  critical: 'Critical',
  important: 'Medium',
  review: 'Low',
};

export const SEVERITY_COLORS: Record<RoadmapSeverity, { bg: string; fg: string; stripe: string }> = {
  // Red — matches 🔴 in Slack
  critical: { bg: '#FEE2E2', fg: '#991B1B', stripe: '#DC2626' },
  // Amber — matches 🟡
  important: { bg: '#FEF3C7', fg: '#92400E', stripe: '#F59E0B' },
  // Green — matches 🟢 (low-pri bug, not a feature)
  review: { bg: '#DCFCE7', fg: '#166534', stripe: '#16A34A' },
};

// For features (severity=NULL) the stripe falls back to a blue tint
// matching the 🔵 dot in Slack.
export const FEATURE_STRIPE_COLOR = '#3B82F6';
// Phase 6 polish v30 (2026-05-15): NEEDS_TRIAGE_STRIPE_COLOR removed.
// The "?" marker + grey stripe + modal checkbox were all dropped in
// the same change; this constant has no remaining consumers.
